import { reloadWithoutPrompt } from "@/shared/utils/reloadWithoutPrompt";

export const PRELOAD_RELOAD_INTERVAL_MS = 10_000;
const STORAGE_KEY = "ottkit:preload-error-reload-at";

/** Перезагружать, если последняя перезагрузка по этой причине была давно. */
export const shouldReloadOnPreloadError = (
  now: number,
  lastReloadAt: number | null
): boolean =>
  lastReloadAt === null || now - lastReloadAt > PRELOAD_RELOAD_INTERVAL_MS;

/**
 * После выката старые чанки удалены с хостинга, а открытая вкладка ещё
 * ссылается на них: ленивый холст автоматизации (React Flow) не
 * загрузится. Vite сообщает об этом событием vite:preloadError;
 * перезагрузка берёт свежий index.html. Не чаще раза в 10 секунд, чтобы
 * не зациклиться, если чанк не грузится по другой причине. sessionStorage
 * может быть недоступен (приватный режим): тогда просто перезагружаем.
 *
 * `preventDefault` вызывается только когда решаем перезагрузить (Review
 * I5): без этого условия событие гасилось всегда, и на второй отказ подряд
 * (лимит перезагрузок исчерпан) `React.lazy` так и не получал настоящий
 * отказ промиса: импорт тихо резолвился в `undefined`, а
 * `AutomationEditorErrorBoundary` (единственный, кто мог бы показать
 * ошибку) её не видел. Оставляя событие как есть, Vite сам довешивает
 * `reason`/повторно бросает отказ, и лежащая ниже граница ошибки его ловит.
 * Перезагрузка идёт через `reloadWithoutPrompt` (Review M11): иначе
 * взведённый `beforeunload`-гард показал бы диалог браузера прямо поверх
 * автоматической перезагрузки, которую партнёр не просил.
 */
export function installPreloadErrorReload(win: Window): void {
  win.addEventListener("vite:preloadError", (event) => {
    const now = Date.now();
    let lastReloadAt: number | null = null;
    try {
      const stored = win.sessionStorage.getItem(STORAGE_KEY);
      lastReloadAt = stored ? Number(stored) : null;
    } catch {
      lastReloadAt = null;
    }
    if (!shouldReloadOnPreloadError(now, lastReloadAt)) return;
    event.preventDefault();
    try {
      win.sessionStorage.setItem(STORAGE_KEY, String(now));
    } catch {
      // Хранилище недоступно: перезагрузка всё равно нужна.
    }
    reloadWithoutPrompt(() => win.location.reload());
  });
}
